import { useEffect, useState } from 'react';

import {
  IdCardWrapper,
  Header,
  IdCardText,
  ModuleTitle,
  ModuleHeader,
  Project,
  Ribbon,
  FastForwardLogo,
  ModuleWrapper,
  TextualAndImageWrapper,
  ViewsAndLayoutImageAndTitleWrapper,
  SubMenusWrapper,
  PrintWrapper,
  ViewsAndLayoutImageTitle,
  MetricsWrapper,
  DimensionsWrapper,
  WeightsWrapper,
  DimensionsImage,
  WeightsImage,
  PreparingForPrintingTitle,
} from './styles';

import Description from '../Description';
import Characteristics from '../Characteristics';
import ViewsAndLayouts from '../ViewsAndLayouts';
import WeightBreakdown from '../WeightBreakdown';
import Lifting from '../Lifting';
import LocationAndCraneCoverage from '../LocationAndCraneCoverage';
import OtherRelevantDocuments from '../OtherRelevantDocuments';
import OtherSimilarCards from '../OtherSimilarCards';
import ProjectNotes from '../ProjectNotes';
import DocumentationList from '../DocumentationList';
import RemainingEngineeringScope from '../RemainingEngineeringScope';
import EquipmentList from '../EquipmentList';
import MainTieIns from '../MainTieIns';
import React, { useRef } from 'react';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';

import weightsImg from '../../resources/weight_img.png';
import dimensionsImg from '../../resources/dimensions_img.png';
import fastForwardLogo from '../../resources/fast4ward.png';
import ImageViewer from '../../components/ImageViewer';
import { useIdCard } from '../../screens/HomeScreen/context/idCard';
import { PrintContextProvider, usePrint } from './context';
import { pick } from 'lodash';

const IdCardForPrint = ({ cardTagId }) => {
  const { idCard: idCardData, idCardImageArray, setPrintMode, setEditingCardTagId } = useIdCard();
  const { printImagesStatusArr, setPrintImagesStatusArr } = usePrint();
  const [valueHasBeenSet, setValueIndicator] = useState(false);
  const componentToPrintRef = useRef();

  useEffect(() => {
    setPrintImagesStatusArr(
      idCardImageArray.map((image) => {
        return {
          imageId: image.imageId,
          loadingStarted: false,
          loadingFinished: false,
        };
      })
    );
    setValueIndicator(true);
  }, [idCardImageArray]);

  useEffect(() => {
    setEditingCardTagId(cardTagId);

    return () => {
      setEditingCardTagId(undefined);
    };
  }, [cardTagId]);

  if (!idCardData || !valueHasBeenSet) {
    return <div />;
  }

  let isReadyForPrint = printImagesStatusArr.every(
    (printImage) => !printImage.imageId || (printImage.loadingStarted && printImage.loadingFinished)
  );

  return (
    <PrintWrapper>
      {isReadyForPrint ? (
        <ReactToPrint
          content={() => componentToPrintRef.current}
          onAfterPrint={() => setPrintMode(false)}
          documentTitle={`Id Card ${idCardData.tagId}`}>
          <PrintContextConsumer>
            {({ handlePrint }) => {
              setTimeout(() => {
                handlePrint();
              }, 100);
              return <div />;
            }}
          </PrintContextConsumer>
        </ReactToPrint>
      ) : (
        <PreparingForPrintingTitle>FETCHING IMAGES FOR PRINTING, PLEASE WAIT...</PreparingForPrintingTitle>
      )}

      <IdCardWrapper ref={componentToPrintRef}>
        <Header>
          <Ribbon />
          <IdCardText>ID Card</IdCardText>
          <Project>{idCardData.project}</Project>
          <FastForwardLogo src={fastForwardLogo} />
        </Header>

        <ModuleWrapper>
          <ModuleHeader>
            <Ribbon />
            <ModuleTitle>MODULE DESCRIPTION</ModuleTitle>
          </ModuleHeader>
          <TextualAndImageWrapper>
            <Description />
            <ViewsAndLayoutImageAndTitleWrapper>
              {
                <ImageViewer
                  imageId={idCardImageArray[0].imageId}
                  containerStyle={{
                    width: '100%',
                    height: '230px',
                    backgroundColor: 'inherit',
                  }}
                />
              }
              <ViewsAndLayoutImageTitle>{idCardImageArray[0].legend}</ViewsAndLayoutImageTitle>
            </ViewsAndLayoutImageAndTitleWrapper>
          </TextualAndImageWrapper>
        </ModuleWrapper>

        <ModuleWrapper>
          <ModuleHeader>
            <Ribbon />
            <ModuleTitle>
              {idCardData.characteristicsLabel && idCardData.characteristicsLabel.length > 0
                ? idCardData.characteristicsLabel
                : 'MODULE CHARACTERISTICS:'}
            </ModuleTitle>
          </ModuleHeader>
          <Characteristics characteristics={idCardData.characteristics} />
        </ModuleWrapper>

        <MetricsWrapper>
          <DimensionsWrapper>
            <DimensionsImage src={dimensionsImg} />
            <table>
              <thead>
                <tr>
                  <th
                    className='big-header'
                    colSpan={3}>
                    MODULE DIMENSIONS
                  </th>
                </tr>
                <tr>
                  <th className='small-header'>Length</th>
                  <th className='small-header'>Width</th>
                  <th className='small-header'>Height</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{idCardData.length}</td>
                  <td>{idCardData.width}</td>
                  <td>{idCardData.height}</td>
                </tr>
              </tbody>
            </table>
          </DimensionsWrapper>

          <WeightsWrapper>
            <WeightsImage src={weightsImg} />
            <table>
              <thead>
                <tr>
                  <th className='big-header'>MODULE WEIGHT</th>
                </tr>
                <tr>
                  <th className='small-header'>DRY</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{idCardData.totalWeightDry}</td>
                </tr>
              </tbody>
            </table>
          </WeightsWrapper>
        </MetricsWrapper>

        <SubMenusWrapper>
          <div className='page-break'>
            <ViewsAndLayouts images={idCardData.images} />
          </div>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>WEIGHT BREAKDOWN</ModuleTitle>
            </ModuleHeader>
            <WeightBreakdown
              printMode={true}
              idCardData={idCardData}
            />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>LIFTING</ModuleTitle>
            </ModuleHeader>
            <Lifting
              liftingLugs={idCardData.liftingLugs}
              liftingPosition={idCardData.liftingPosition}
            />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>LOCATION {'&'} CRANE COVERAGE</ModuleTitle>
            </ModuleHeader>
            <LocationAndCraneCoverage images={idCardData.images} />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>MAIN TIE-INS {'&'} INTERFACES</ModuleTitle>
            </ModuleHeader>
            <MainTieIns
              {...pick(idCardData, ['tieIns', 'tieInsComment', 'tieInsOtherUtilities', 'tieInsOtherNetworks'])}
            />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>KEY EQUIPMENT LIST</ModuleTitle>
            </ModuleHeader>
            <EquipmentList
              printMode={true}
              {...pick(idCardData, ['equipmentList', 'equipmentListComment'])}
            />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>DOCUMENTATION LIST</ModuleTitle>
            </ModuleHeader>
            <DocumentationList documents={idCardData.documents} />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>OTHER RELEVANT DOCUMENTS</ModuleTitle>
            </ModuleHeader>
            <OtherRelevantDocuments otherDocuments={idCardData.otherDocuments} />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>REMAINING ENGINEERING SCOPE</ModuleTitle>
            </ModuleHeader>
            <RemainingEngineeringScope images={idCardData.images} />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>OTHER SIMILAR CARDS</ModuleTitle>
            </ModuleHeader>
            <OtherSimilarCards otherSimilarIdCards={idCardData.otherSimilarIdCards} />
          </ModuleWrapper>

          <ModuleWrapper>
            <ModuleHeader>
              <Ribbon />
              <ModuleTitle>PROJECT NOTES</ModuleTitle>
            </ModuleHeader>
            <ProjectNotes resNotes={idCardData.resNotes} />
          </ModuleWrapper>
        </SubMenusWrapper>
      </IdCardWrapper>
    </PrintWrapper>
  );
};

const IdCardForPrintWrapped = ({ cardTagId }) => (
  <PrintContextProvider>
    <IdCardForPrint cardTagId={cardTagId} />
  </PrintContextProvider>
);

export default IdCardForPrintWrapped;
